import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Icon } from 'semantic-ui-react';
import FormInput from '../../../components/ui/FormInput';
import passwordValidator from 'password-validator';

// Create a schema for password validation
const createPasswordSchema = () => {
  const schema = new passwordValidator();
  
  // Add properties to the schema
  schema
    .is().min(6)                                   // Minimum length 6
    .is().max(100)                                 // Maximum length 100
    .has().uppercase()                             // Must have uppercase letters
    .has().lowercase()                             // Must have lowercase letters
    .has().digits(1)                               // Must have at least 1 digit
    .has().not().spaces()                          // Should not have spaces
    .is().not().oneOf(['password', 'Password']);   // Blacklist these values
  
  return schema;
};

const schema = createPasswordSchema();

const PasswordInput = ({ 
  label, 
  value, 
  onChange, 
  showValidation = false, 
  validationMessage,
  userLang,
  onValidationChange,
  className,
  ...props 
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [touched, setTouched] = useState(false);
  
  // Use a ref to store the latest validation callback to avoid dependency issues
  const validationChangeRef = useRef(onValidationChange);
  useEffect(() => {
    validationChangeRef.current = onValidationChange;
  }, [onValidationChange]);
  
  // Validate password and notify parent (extracted to a stable function)
  const validatePassword = useCallback((password, shouldNotify = true) => {
    if (!showValidation) return [];
    
    const results = password ? schema.validate(password, { list: true }) : ['min', 'uppercase', 'lowercase', 'digits'];
    
    // Only update state if the results have changed
    if (JSON.stringify(results) !== JSON.stringify(validationErrors)) {
      setValidationErrors(results);
      
      // Notify parent if needed and callback exists
      if (shouldNotify && validationChangeRef.current) {
        validationChangeRef.current(results);
      }
    }
    
    return results;
  }, [showValidation, validationErrors]);
  
  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };
  
  const handleChange = (e) => {
    if (!touched) {
      setTouched(true);
    }
    
    onChange(e);
    
    // Validate on change
    if (showValidation) {
      validatePassword(e.target.value);
    }
  };
  
  const handleBlur = () => {
    if (!touched) {
      setTouched(true);
    }
    
    // Revalidate on blur
    if (showValidation && value) {
      validatePassword(value);
    }
  };
  
  // Initial validation on mount and when value/showValidation changes
  useEffect(() => {
    // Skip validation if not needed
    if (!showValidation) return;
    
    // Only validate when touched
    if (touched) {
      validatePassword(value);
    } else if (validationChangeRef.current) {
      // For untouched fields, reset validation
      validationChangeRef.current([]);
    }
    
  }, [value, showValidation, touched, validatePassword]);
  
  // Generate error message from validation errors
  const getValidationErrorMessage = () => {
    // Don't show validation errors until the field has been touched
    if (!touched || !showValidation || validationErrors.length === 0) return props.error;
    
    // Map validation failures to user-friendly messages
    const errorMessages = {
      min: userLang === 'zh-CN' ? '密码长度至少为6位' : 'Password must be at least 6 characters',
      max: userLang === 'zh-CN' ? '密码长度不能超过100位' : 'Password must be less than 100 characters',
      uppercase: userLang === 'zh-CN' ? '密码必须包含大写字母' : 'Password must include uppercase letters',
      lowercase: userLang === 'zh-CN' ? '密码必须包含小写字母' : 'Password must include lowercase letters',
      digits: userLang === 'zh-CN' ? '密码必须包含数字' : 'Password must include at least 1 digit',
      spaces: userLang === 'zh-CN' ? '密码不能包含空格' : 'Password cannot contain spaces',
      oneOf: userLang === 'zh-CN' ? '密码太简单' : 'Password is too common'
    };
    
    // Return the first error message or a custom message if provided
    if (validationMessage) {
      return validationMessage;
    }
    
    const firstError = validationErrors[0];
    return errorMessages[firstError] || props.error;
  };
  
  return (
    <div>
      <FormInput
        label={label}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        error={getValidationErrorMessage()}
        type={showPassword ? "text" : "password"}
        icon={{
          name: showPassword ? 'eye slash' : 'eye',
          link: true,
          onClick: toggleShowPassword
        }}
        iconPosition="right"
        className={className}
        fluid
        {...props}
      />
      
      {/* Password strength indicator - only show when showValidation is true and field has been touched */}
      {showValidation && value && touched && (
        <div className="mt-1">
          {/* Password strength text */}
          <p className="text-xs text-gray-500 mb-1">
            {validationErrors.length === 0 ? 
              (userLang === 'zh-CN' ? '密码强度: 强' : 'Password strength: Strong') : 
              validationErrors.length <= 2 ? 
                (userLang === 'zh-CN' ? '密码强度: 中' : 'Password strength: Medium') : 
                (userLang === 'zh-CN' ? '密码强度: 弱' : 'Password strength: Weak')}
          </p>
          
          {/* Password strength bars */}
          <div className="flex items-center space-x-1">
            <div className={`h-1 flex-1 rounded-full ${validationErrors.length === 0 ? 'bg-green-500' : 
                              validationErrors.length <= 2 ? 'bg-yellow-500' : 'bg-red-500'}`}></div>
            <div className={`h-1 flex-1 rounded-full ${validationErrors.length === 0 ? 'bg-green-500' : 
                              validationErrors.length <= 1 ? 'bg-yellow-500' : 'bg-gray-200'}`}></div>
            <div className={`h-1 flex-1 rounded-full ${validationErrors.length === 0 ? 'bg-green-500' : 'bg-gray-200'}`}></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PasswordInput; 